import { useState, useCallback, useMemo } from "react";
import { useMutation } from "@apollo/client";
import { ADD_FAVORITE, REMOVE_FAVORITE, FAVORITES } from "../favorites.queries";
import { useFavorites } from "./useFavorites";
import {
  AddFavoriteMutation,
  AddFavoriteMutationVariables,
} from "../../../__generated__/AddFavoriteMutation";
import {
  RemoveFavoriteMutation,
  RemoveFavoriteMutationVariables,
} from "../../../__generated__/RemoveFavoriteMutation";

export const useFavoriteToggle = () => {
  const { data, loading: loadingFavorites } = useFavorites();
  const [isToggling, setIsToggling] = useState(false);

  const [addFavorite] = useMutation<
    AddFavoriteMutation,
    AddFavoriteMutationVariables
  >(ADD_FAVORITE, {
    refetchQueries: [{ query: FAVORITES }],
    awaitRefetchQueries: true,
  });

  const [removeFavorite] = useMutation<
    RemoveFavoriteMutation,
    RemoveFavoriteMutationVariables
  >(REMOVE_FAVORITE, {
    refetchQueries: [{ query: FAVORITES }],
    awaitRefetchQueries: true,
  });

  // Créer un Set des IDs de produits favoris pour une recherche rapide
  const favoriteProductIds = useMemo(() => {
    if (!data?.favorites?.results) {
      console.log("useFavoriteToggle - Pas de données favorites:", data);
      return new Set<number>();
    }
    const productIds = data.favorites.results.map((fav) => fav.productId);
    console.log("useFavoriteToggle - Product IDs des favoris:", productIds);
    return new Set(productIds);
  }, [data]);

  // Créer un Map pour accéder rapidement à l'ID du favori depuis le productId
  const favoriteIdByProductId = useMemo(() => {
    if (!data?.favorites?.results) return new Map<number, number>();
    const map = new Map<number, number>();
    data.favorites.results.forEach((fav) => {
      if (fav.productId && fav.id) map.set(fav.productId, fav.id);
    });
    return map;
  }, [data]);

  // Vérifier si un produit est en favori
  const isFavorite = useCallback(
    (productId: number) => {
      const result = favoriteProductIds.has(productId);
      console.log(`isFavorite(${productId}):`, result, "Set size:", favoriteProductIds.size);
      return result;
    },
    [favoriteProductIds]
  );

  // Toggle un favori
  const toggleFavorite = useCallback(
    async (productId: number) => {
      setIsToggling(true);
      try {
        if (isFavorite(productId)) {
          // Retirer des favoris
          const favoriteId = favoriteIdByProductId.get(productId);
          if (favoriteId) {
            const { data: result } = await removeFavorite({
              variables: { id: favoriteId },
            });
            if (result?.removeFavorite?.ok) {
              return true;
            }
          }
          return false;
        } else {
          // Ajouter aux favoris
          const { data: result } = await addFavorite({
            variables: { input: { productId } },
          });
          if (result?.addFavorite?.ok) {
            return true;
          }
          return false;
        }
      } catch (error) {
        console.error("Erreur lors du toggle du favori:", error);
        return false;
      } finally {
        setIsToggling(false);
      }
    },
    [isFavorite, favoriteIdByProductId, addFavorite, removeFavorite]
  );

  return {
    isFavorite,
    toggleFavorite,
    loading: loadingFavorites || isToggling,
    isToggling,
  };
};
